import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import EphemeralMessage from '../StudentSingleSkill/EphemeralMessage';
import {
  CurrentSkillResult,
  shouldShowCurrentSkillStatus,
  SKILL_SUBSTATE_STYLES,
} from '../../custom-hooks/useCurrentSkillResult';
import { EXIT_SLIP_DEMONSTRATED_LABEL } from '../../businessLogicService/exitSlipSkillPage';
import { Toolbar } from 'primereact/toolbar';
import { CurrentStatus, PreviousStatus } from '../StudentSingleSkill/StudentSingleSkill';
import { PreviousAttempts } from '../../models/PreviousAttempts';
import { Tooltip } from 'primereact/tooltip';
import { RatingDisplay, SkillStatusBadge } from '../common';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Divider } from 'primereact/divider';
import { format, parseISO } from 'date-fns';
import { StudentSingleSkillQuery$data } from '../../graphQLOperations/__generated__/StudentSingleSkillQuery.graphql';
import SkillResourceHeaderLearnosityWrapper from './SkillResourceHeaderLearnosityWrapper';
import { DataLrnSessionBySkill } from '../../custom-hooks/useLrnSession';
import RevisitSkill from '../../assets/revisit-skill.svg';
import ExitTicketIcon from '../../assets/exit-ticket-icon-xs.svg?react';
import SkillHeaderExitSlipChip from './SkillHeaderExitSlipChip';
import SkillHeaderExitSlipMessage from './SkillHeaderExitSlipMessage';
import type { SkillExitSlipState } from '../../custom-hooks/useSkillExitSlipState';
import { useWindowSize } from '../../custom-hooks/useWindowSize';

interface Props {
  currentStatus: CurrentStatus | null;
  previousAttempts: PreviousAttempts[];
  currentSkillResult: CurrentSkillResult;
  previousStatus: PreviousStatus[];
  skillName: string;
  allUserActions: StudentSingleSkillQuery$data['allUserActions'];
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  disabledReviewModal: boolean;
  exitSlipState: SkillExitSlipState;
  skillCode: string;
}

const SkillResourceHeader: FC<Props> = ({
  currentStatus,
  currentSkillResult,
  previousAttempts,
  previousStatus,
  skillName,
  allUserActions,
  expanded,
  setExpanded,
  disabledReviewModal,
  exitSlipState,
  skillCode,
}) => {
  const [isDropdown, setIsDropdown] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [lrnData, setLrnData] = useState<DataLrnSessionBySkill | null>(null);
  const [showRevisitMessage, setShowRevisitMessage] = useState(false);
  const [revisitMessageKey, setRevisitMessageKey] = useState(0);
  const [revisitMessageDelay, setRevisitMessageDelay] = useState(2);

  // A skill won on exit tickets has a result to show whatever the substate says — and for the first
  // stretch of its life the substate says nothing. The student finishes their last ticket, the skill
  // is theirs, and the Inference Engine has yet to record it: until then the state still reads
  // NOT_TESTED, which shouldShowCurrentSkillStatus answers 'no result to show' to (rightly — it has no
  // way of knowing about exit tickets). That is exactly the moment this screen is about, so the
  // exit-ticket reading overrides it rather than waiting for the backend to agree.
  const showStatus =
    exitSlipState.inferredViaExitSlip ||
    ((currentStatus || currentSkillResult) && shouldShowCurrentSkillStatus(currentSkillResult, currentStatus));
  const isReviewModalElegible = !disabledReviewModal && previousAttempts.length > 0 && previousAttempts[0].sessionId;

  useEffect(() => {
    setIsDropdown(
      previousStatus.length > 1 ||
        (previousStatus.length === 1 && previousStatus[0].id !== currentSkillResult.skillState?.skillAssessment?.id),
    );
  }, [currentSkillResult]);

  const handleOpenReviewModal = () => {
    //different behavior than AssessmentWrapper to trigger review modal from AssessmentCompleted
    setLrnData({
      sessionId: previousAttempts[0].sessionId ?? '',
      state: 'review',
      activityId: previousAttempts[0].activityId ?? '',
      showIncorrectAnswers: true,
    });
    setOpenModal(true);
  };

  const isRevisitSkill =
    currentSkillResult?.iconType === 'revisit' ||
    currentSkillResult?.status === 'Revisit' ||
    currentStatus?.status === 'Revisit';
  const shouldShowRevisitHeader = isRevisitSkill && currentSkillResult?.hasScore && currentSkillResult?.score >= 8;

  useEffect(() => {
    if (!shouldShowRevisitHeader || !skillName) return;

    const hasSeenRevisitMessage = sessionStorage.getItem(`revisit-message-seen-${skillName}`);

    if (hasSeenRevisitMessage) return;

    setRevisitMessageDelay(2);
    setShowRevisitMessage(true);
    setRevisitMessageKey(prev => prev + 1);
    sessionStorage.setItem(`revisit-message-seen-${skillName}`, 'true');
  }, [shouldShowRevisitHeader, skillName]);

  useEffect(() => {
    if (!showRevisitMessage) return;
    const handleCloseRevisitMessage = () => {
      setShowRevisitMessage(false);
    };
    document.addEventListener('pointerdown', handleCloseRevisitMessage);
    return () => {
      document.removeEventListener('pointerdown', handleCloseRevisitMessage);
    };
  }, [showRevisitMessage]);

  const revisitTooltip = 'This is a refresher for a skill you previously passed.';

  const revisitLabel = shouldShowRevisitHeader && (
    <div className="tw-flex tw-items-center tw-justify-end tw-w-full tw-pr-8">
      <Tooltip target=".nc-revisit-skill-badge" position="top" />
      <button
        type="button"
        className="nc-revisit-skill-badge tw-inline-flex tw-items-center tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
        data-pr-tooltip={revisitTooltip}
        data-pr-position="top"
        aria-label="Show Revisit this skill message"
      >
        <SkillStatusBadge
          status="Revisit this"
          showIcon
          iconType="revisit"
          className="tw-inline-flex tw-items-center tw-gap-1 tw-text-indigo-500 tw-font-bold tw-text-base"
        />
      </button>
    </div>
  );
  // The exit-ticket chip, and — for a skill won on them — the panel the design hangs off "Your
  // Result". Both read the one state object the page resolved, so neither can disagree with the Skill
  // Challenge screen about the same skill.
  const exitSlipChip = exitSlipState.hasTicketProgress && exitSlipState.chipLabel && (
    <SkillHeaderExitSlipChip label={exitSlipState.chipLabel} isOnFire={!!exitSlipState.summary?.isOnFire} />
  );

  // Hung off "Your Result" itself, as the design draws it. Stood down when the review tooltip is in
  // play: the two would be two PrimeReact tooltips bound to one target class, and the last one mounted
  // wins. In practice they do not meet — a skill demonstrated on exit tickets has no Skill Challenge
  // attempt to review — but the result block is shared ground and the next skill state to reach it
  // should not have to rediscover this.
  const exitSlipResultTooltip = exitSlipState.inferredViaExitSlip && !isReviewModalElegible && (
    <Tooltip target=".nc_skill-result-dropdown-title" position="bottom" className="tw-w-60">
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-font-atkinson">
        <span className="tw-font-bold">Already done</span>
        <span>You completed this skill from Exit ticket work</span>
        {!!exitSlipState.progress?.relatedCount && (
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <ExitTicketIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
            {exitSlipState.summary?.passed.count}/{exitSlipState.progress.relatedCount} Exit tickets
          </span>
        )}
      </div>
    </Tooltip>
  );

  const resultDropdown = showStatus && (
    <>
      {isReviewModalElegible && (
        <Tooltip
          target=".nc_skill-result-dropdown-title"
          pt={{ root: { style: { transform: 'translateY(-20px)' } } }}
        />
      )}
      <div className={`nc_skill-result-dropdown-wrap ${expanded ? 'expanded' : ''}`}>
        <div className="nc_skill-result-dropdown">
          <div
            className={`nc_skill-result-dropdown-title tw-text-[20px] tw-font-bold ${
              isReviewModalElegible ? 'tw-cursor-pointer' : ''
            }`}
            data-pr-tooltip="Review last skill challenge result"
            data-pr-position="top"
            role="button"
            tabIndex={!isReviewModalElegible ? -1 : 0}
            onClick={() => {
              isReviewModalElegible && handleOpenReviewModal();
            }}
            onKeyDown={e => {
              if (!isReviewModalElegible) return;

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenReviewModal();
              }
            }}
          >
            Your Result: &nbsp;&nbsp;
            {exitSlipState.inferredViaExitSlip ? (
              // Said here rather than left to the substate, and ahead of the score: a skill can carry
              // a failed Skill Challenge from before its tickets were finished, and the stars for that
              // attempt are no longer the answer to "your result" — the skill is theirs.
              <SkillStatusBadge
                status={EXIT_SLIP_DEMONSTRATED_LABEL}
                className={`nc_skill-result-mini !tw-text-lg ${SKILL_SUBSTATE_STYLES.DONE_BY_EXIT_TICKETS}`}
                showIcon
                iconType="check-circle"
              />
            ) : currentSkillResult &&
              currentSkillResult.hasScore &&
              currentSkillResult.source !== 'fallback' &&
              previousAttempts.length > 0 ? (
              <RatingDisplay
                score={currentSkillResult.score}
                maxScore={currentSkillResult.maxScore}
                className="!tw-text-lg"
              />
            ) : (
              <SkillStatusBadge
                status={currentSkillResult?.status || currentStatus?.status || ''}
                className={`nc_skill-result-mini !tw-text-lg ${
                  currentSkillResult?.statusClassName || currentStatus?.classname
                }`}
                showIcon={currentSkillResult?.showIcon}
                iconType={currentSkillResult?.iconType}
              />
            )}
          </div>
          {isDropdown &&
            (expanded ? (
              <ChevronUp
                className="tw-text-indigo-500 tw-cursor-pointer"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpanded(!expanded);
                  }
                }}
              />
            ) : (
              <ChevronDown
                className="tw-text-indigo-500 tw-cursor-pointer"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpanded(!expanded);
                  }
                }}
              />
            ))}
        </div>
        <Divider
          className={`${expanded ? 'tw-block' : 'tw-hidden'} tw-my-0 tw-mx-3 tw-w-auto tw-h-[2px] tw-bg-neutral-100`}
        />
        {expanded &&
          // remove the first element because its the same of the currentStatus
          previousStatus.slice(1).map((el, key) => (
            <div key={key} className="nc_skill-result-dropdown-items">
              <span className="tw-text-xs tw-font-atkinson tw-text-neutral-700 tw-mr-4 ">
                {format(parseISO(el.date), 'MMM d, yyyy - h:mm a')}
              </span>
              <RatingDisplay score={el.score} maxScore={el.maxScore} className="!tw-text-lg" />
            </div>
          ))}
      </div>
    </>
  );

  // The two messages this header already owns: the Revisit explanation and the one-off pointer at the
  // review dropdown. Either of them occupying the corner holds the exit-ticket message back.
  //
  // A Revisit header holds it back whether or not its own message plays, which it only does once per
  // session: that header hands the row to the Revisit badge and drops the chip entirely, and the
  // exit-ticket message is the chip's companion — it has nothing to sit beside there.
  const hasOtherEphemeralMessage =
    shouldShowRevisitHeader || (!isRevisitSkill && showStatus && previousAttempts.length === 1);

  // Which of the two slots below holds the message, decided here rather than by rendering it into both
  // and hiding one with `lg:`. It has to be one instance: the monster is an inlined Figma SVG with
  // hardcoded ids, and a second copy — even behind `display: none` — captures the ids and strips the
  // visible one of its mask, filter and gradient (see SkillHeaderExitSlipMessage).
  //
  // The 1024 is `.nc_skill-result-header`'s own column/row breakpoint, restated here because a media
  // query cannot pick a parent for a React node. The two have to move together.
  const { width } = useWindowSize();
  const isHeaderRow = width >= 1024;

  const exitSlipMessage = !!exitSlipState.messageVariant && !hasOtherEphemeralMessage && (
    <SkillHeaderExitSlipMessage variant={exitSlipState.messageVariant} skillCode={skillCode} />
  );

  // Below 1024px the header stacks, so the message rides on the title's row, to its right — the chip
  // has moved to a row of its own by then. Above it, the row is title | chip | result: the message
  // goes in ahead of the chip and flexbox keeps it on the chip's left whether or not a result follows.
  const center = (
    <div className="tw-flex tw-flex-col">
      <div className="tw-flex tw-items-center tw-gap-4">
        <h1>{skillName}</h1>
        {!isHeaderRow && exitSlipMessage}
      </div>
      {allUserActions?.nodes?.length ? (
        <span className="tw-font-atkinson tw-text-xs tw-text-neutral-700">
          Last visited: {format(parseISO(allUserActions?.nodes[0].timeStamp), 'MMM d, yyyy - h:mm a')}
        </span>
      ) : (
        <></>
      )}
    </div>
  );

  // The chip travels with the result rather than instead of it, and does not need one to appear: a
  // skill with tickets done but never assessed has no status to show (shouldShowCurrentSkillStatus
  // says so) and still has tickets worth counting.
  const end = !shouldShowRevisitHeader && (exitSlipChip || resultDropdown) && (
    <div className="tw-flex tw-items-center tw-gap-4">
      {isHeaderRow && exitSlipMessage}
      {exitSlipChip}
      {exitSlipResultTooltip}
      {resultDropdown}
    </div>
  );

  return (
    <>
      <div className="col-12 col-offset-0 tw-pb-5">
        <Toolbar
          start={center}
          center={shouldShowRevisitHeader ? revisitLabel : undefined}
          end={end}
          className={`nc_skill-result-header nc_page-header ${showStatus ? '' : ' !tw-h-auto lg:!tw-h-[90px]'}`}
          pt={{
            start: {
              style: {
                position: `${expanded ? 'absolute' : ''}`,
              },
            },
          }}
        />
      </div>
      {shouldShowRevisitHeader && showRevisitMessage && (
        <EphemeralMessage
          key={revisitMessageKey}
          delay={revisitMessageDelay}
          text={
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
              <img src={RevisitSkill} alt="Revisit Skill" />
              <span>
                You’ve passed this skill before, but it looks like you <b>need a little reinforcement.</b>
              </span>
              Try again to stay on top of your Roadmap progress!
            </div>
          }
          ease={[0.34, 1.56, 0.64, 1]}
          duration={1}
          customStyle={{
            top: '240px',
            right: '250px',
            minWidth: '402px',
          }}
          strokePosition={{
            top: '-22px',
            right: '-2px',
            left: null,
          }}
          strokeClassName="tw-scale-x-[-1]"
        />
      )}
      {!isRevisitSkill && showStatus && previousAttempts.length === 1 && (
        <EphemeralMessage
          delay={1}
          text="Now you can review your last answers here"
          ease={[0.34, 1.56, 0.64, 1]}
          duration={1}
        />
      )}
      {openModal && <SkillResourceHeaderLearnosityWrapper lrnData={lrnData} setOpenLearnosityModal={setOpenModal} />}
    </>
  );
};

export default SkillResourceHeader;
